import { ColumnType, ReportColumn } from "@/types/evaluate";
import { ExclamationIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import React from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { HeaderContent } from "./HeaderContent";
import { usePromptRegistryObjects } from "@/queries";
import { useAuth } from "@/context/auth-context";

interface DefaultColumnCellProps {
  column: ReportColumn;
}

export const DefaultColumnCell: React.FC<DefaultColumnCellProps> = ({
  column,
}) => {
  const { workspaceId } = useParams();
  const versionNumber = column?.configuration?.template?.version_number;
  const promptName = column?.configuration?.template?.name;
  let promptId = column?.configuration?.frozen_template_data?.prompt_id;
  const auth = useAuth();

  const { data: promptRegistryObjects } = usePromptRegistryObjects(
    auth!.userToken!,
    {
      workspaceId: Number(workspaceId),
    },
  );

  if (!promptId) {
    const prompt = promptRegistryObjects?.pages
      .flatMap((page) => page.items)
      .find((prompt) => prompt.prompt_name === promptName);
    promptId = prompt?.id;
  }

  const baseURL = `/workspace/${workspaceId}/prompt/${promptId}`;

  const url = versionNumber ? `${baseURL}/version/${versionNumber}` : baseURL;

  const errorMessage = column.errors?.map((error) => error.message).join("; ");

  const hasExternalLinkIcon = column.column_type === ColumnType.PROMPT_TEMPLATE;

  return (
    <div className="group relative flex h-full w-full items-center px-3 py-2">
      <div className="flex h-full w-full items-center justify-center space-x-1">
        {errorMessage && (
          <BasicTooltip content={errorMessage}>
            <ExclamationIcon className="h-7 w-7 text-red-600" />
          </BasicTooltip>
        )}
        <div
          className={`flex items-center justify-center ${
            hasExternalLinkIcon ? "px-8" : ""
          }`}
        >
          <HeaderContent column={column} />
        </div>
      </div>
      {hasExternalLinkIcon && (
        <div className="absolute right-2">
          <BasicTooltip content={`Open ${promptName} in new tab`}>
            <Button
              size="tinyIcon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => window.open(url, "_blank")}
            >
              <ExternalLinkIcon className="h-4 w-4 text-gray-600" />
            </Button>
          </BasicTooltip>
        </div>
      )}
    </div>
  );
};
